import { useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, User, Phone, KeyRound, Image as ImageIcon, Upload, Loader2, Check, Camera, Trash2 } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Master admin profile — big modal opened from the header bell area.
 * Lets the owner:
 *   • upload / change / remove profile photo
 *   • upload / change / remove background image
 *   • rename himself + change phone (optional — 11-digit validated)
 *   • change password (requires current)
 * Backend: `PUT /api/admin/master-profile` + `POST /api/admin/upload-profile-photo`.
 * Requested Feb 27, 2026.
 */
const MasterProfileModal = ({ user, onClose, onSaved }) => {
  const photoRef = useRef(null);
  const bgRef = useRef(null);
  const [profilePhoto, setProfilePhoto] = useState(user?.profile_photo || "");
  const [backgroundPhoto, setBackgroundPhoto] = useState(user?.background_photo || "");
  const [fullName, setFullName] = useState(user?.full_name || "");
  const [phone, setPhone] = useState(user?.phone || "");
  const [currentPwd, setCurrentPwd] = useState("");
  const [newPwd, setNewPwd] = useState("");
  const [uploading, setUploading] = useState(false);
  const [uploadingBg, setUploadingBg] = useState(false);
  const [saving, setSaving] = useState(false);

  const uploadFile = async (file, slot) => {
    if (!file) return;
    const setBusy = slot === "photo" ? setUploading : setUploadingBg;
    setBusy(true);
    try {
      const form = new FormData();
      form.append("file", file);
      const { data } = await axios.post(`${API}/admin/upload-profile-photo`, form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      if (slot === "photo") setProfilePhoto(data.path);
      else setBackgroundPhoto(data.path);
      toast.success("تم رفع الصورة");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "فشل رفع الصورة");
    } finally { setBusy(false); }
  };

  const save = async () => {
    const body = {};
    if (profilePhoto !== (user?.profile_photo || "")) body.profile_photo = profilePhoto;
    if (backgroundPhoto !== (user?.background_photo || "")) body.background_photo = backgroundPhoto;
    // Master account is locked — name, phone, password cannot be changed
    // by this modal per owner's explicit request. Only sub-admins can edit them.
    if (user?.is_sub_admin) {
      if (fullName.trim() && fullName.trim() !== user?.full_name) body.full_name = fullName.trim();
      if (phone.trim() && phone.trim() !== user?.phone) {
        if (!/^\d{11}$/.test(phone.trim())) { toast.error("رقم الهاتف يجب أن يكون 11 رقم"); return; }
        body.phone = phone.trim();
      }
      if (newPwd) {
        if (newPwd.length < 4) { toast.error("كلمة السر يجب 4 أحرف على الأقل"); return; }
        if (!currentPwd) { toast.error("اكتب كلمة السر الحالية"); return; }
        body.new_password = newPwd;
        body.current_password = currentPwd;
      }
    }
    if (Object.keys(body).length === 0) { toast.info("لا توجد تعديلات للحفظ"); return; }
    setSaving(true);
    try {
      // Sub-admins hit /admin/my-profile (self-scope). Master hits
      // /admin/master-profile (behavior identical, but wraps require_master_admin).
      const endpoint = user?.is_sub_admin ? `${API}/admin/my-profile` : `${API}/admin/master-profile`;
      await axios.put(endpoint, body);
      toast.success("✅ تم حفظ الملف الشخصي");
      onSaved?.();
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "فشل الحفظ");
    } finally { setSaving(false); }
  };

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
        className="fixed inset-0 z-[300] bg-black/60 backdrop-blur-sm flex items-center justify-center p-3"
        onClick={onClose}
        data-testid="master-profile-overlay"
      >
        <motion.div
          initial={{ scale: 0.9, y: 20, opacity: 0 }}
          animate={{ scale: 1, y: 0, opacity: 1 }}
          onClick={(e) => e.stopPropagation()}
          data-testid="master-profile-modal"
          className="bg-white rounded-3xl shadow-2xl w-full max-w-lg max-h-[92vh] overflow-hidden flex flex-col"
          dir="rtl"
        >
          {/* Cover with background preview */}
          <div
            className="relative h-32 bg-gradient-to-l from-[#7C3AED] via-[#2563EB] to-[#0EA5E9]"
            style={backgroundPhoto ? { backgroundImage: `url(${API}/files/${backgroundPhoto})`, backgroundSize: "cover", backgroundPosition: "center" } : {}}
          >
            <div className="absolute inset-0 bg-black/25" />
            <button
              onClick={onClose}
              data-testid="master-profile-close"
              className="absolute top-3 left-3 bg-white/25 hover:bg-white/40 backdrop-blur rounded-full p-1.5 text-white transition-colors z-10"
            >
              <X className="w-4 h-4" />
            </button>
            <div className="absolute top-3 right-3 flex gap-1.5 z-10">
              <button
                onClick={() => bgRef.current?.click()}
                data-testid="master-profile-bg-upload"
                className="bg-white/25 hover:bg-white/40 backdrop-blur text-white text-[11px] font-bold px-2.5 py-1 rounded-full flex items-center gap-1"
              >
                {uploadingBg ? <Loader2 className="w-3 h-3 animate-spin" /> : <ImageIcon className="w-3 h-3" />}
                خلفية
              </button>
              {backgroundPhoto && (
                <button
                  onClick={() => setBackgroundPhoto("")}
                  data-testid="master-profile-bg-remove"
                  className="bg-[#E85D45]/80 hover:bg-[#E85D45] text-white text-[11px] font-bold p-1 rounded-full"
                  title="إزالة الخلفية"
                >
                  <Trash2 className="w-3 h-3" />
                </button>
              )}
            </div>
            <input
              ref={bgRef}
              type="file"
              accept="image/*"
              className="hidden"
              onChange={(e) => uploadFile(e.target.files?.[0], "bg")}
            />
            {/* Profile avatar overlapping cover */}
            <div className="absolute -bottom-10 right-1/2 translate-x-1/2">
              <div className="relative">
                <div className="w-24 h-24 rounded-full border-4 border-white bg-gradient-to-br from-[#F5A623] to-[#E85D45] text-white flex items-center justify-center font-black text-3xl overflow-hidden shadow-lg">
                  {profilePhoto ? (
                    <img src={`${API}/files/${profilePhoto}`} alt="me" className="w-full h-full object-cover" />
                  ) : (
                    (fullName || "?")[0]
                  )}
                  {uploading && (
                    <div className="absolute inset-0 bg-black/40 flex items-center justify-center">
                      <Loader2 className="w-6 h-6 animate-spin text-white" />
                    </div>
                  )}
                </div>
                <button
                  onClick={() => photoRef.current?.click()}
                  data-testid="master-profile-photo-upload"
                  className="absolute bottom-0 right-0 bg-[#3B82F6] hover:bg-[#2563EB] text-white rounded-full p-1.5 shadow-md border-2 border-white"
                  title="تغيير صورة البروفايل"
                >
                  <Camera className="w-3.5 h-3.5" />
                </button>
                {profilePhoto && (
                  <button
                    onClick={() => setProfilePhoto("")}
                    data-testid="master-profile-photo-remove"
                    className="absolute bottom-0 left-0 bg-[#E85D45] hover:bg-[#C24A35] text-white rounded-full p-1.5 shadow-md border-2 border-white"
                    title="إزالة الصورة"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                )}
                <input
                  ref={photoRef}
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={(e) => uploadFile(e.target.files?.[0], "photo")}
                />
              </div>
            </div>
          </div>

          {/* Body */}
          <div className="flex-1 overflow-y-auto pt-14 px-5 pb-4 space-y-3">
            {/* Lock notice — the master's identity is immutable */}
            {!user?.is_sub_admin && (
              <div className="bg-[#FEF3C7] border-2 border-[#F59E0B] rounded-2xl p-2.5 flex items-start gap-2">
                <div className="text-lg">🔒</div>
                <p className="text-[11px] font-bold text-[#78350F] leading-snug">
                  الاسم ورقم الهاتف وكلمة السر للحساب الرئيسي مقفلة — تقدر بس تغيّر الصورة الشخصية والخلفية.
                </p>
              </div>
            )}
            <div>
              <label className="text-xs font-bold text-[#1E3A5F] mb-1 flex items-center gap-1">
                <User className="w-3.5 h-3.5" /> الاسم الكامل
              </label>
              <input
                value={fullName}
                onChange={(e) => setFullName(e.target.value)}
                data-testid="master-profile-name"
                disabled={!user?.is_sub_admin}
                className={`w-full px-3 py-2 rounded-xl border border-[#E4EBE6] focus:outline-none focus:ring-2 focus:ring-[#7C3AED] text-sm ${!user?.is_sub_admin ? "bg-[#F3F6F4] text-[#5C6E64] cursor-not-allowed" : ""}`}
                placeholder="اسمك الكامل"
              />
            </div>
            <div>
              <label className="text-xs font-bold text-[#1E3A5F] mb-1 flex items-center gap-1">
                <Phone className="w-3.5 h-3.5" /> رقم الهاتف
              </label>
              <input
                value={phone}
                onChange={(e) => setPhone(e.target.value.replace(/\D/g, "").slice(0, 11))}
                maxLength={11}
                inputMode="numeric"
                data-testid="master-profile-phone"
                disabled={!user?.is_sub_admin}
                className={`w-full px-3 py-2 rounded-xl border text-sm focus:outline-none focus:ring-2 ${
                  !user?.is_sub_admin
                    ? "bg-[#F3F6F4] text-[#5C6E64] cursor-not-allowed border-[#E4EBE6]"
                    : phone && phone.length !== 11
                    ? "border-[#E85D45] focus:ring-[#E85D45]"
                    : "border-[#E4EBE6] focus:ring-[#7C3AED]"
                }`}
                placeholder="07XXXXXXXXX"
              />
              {user?.is_sub_admin && phone && phone.length !== 11 && (
                <p className="text-[10px] text-[#E85D45] font-bold mt-1">
                  ⚠️ رقم الهاتف يجب أن يكون 11 رقم بالضبط ({phone.length}/11)
                </p>
              )}
            </div>

            {/* Password change — only sub-admins */}
            {user?.is_sub_admin && (
              <div className="bg-[#FEF3C7] rounded-2xl border-2 border-[#F59E0B]/50 p-3 space-y-2">
                <p className="text-xs font-black text-[#78350F] flex items-center gap-1">
                  <KeyRound className="w-3.5 h-3.5" /> تغيير كلمة السر (اختياري)
                </p>
                <input
                  type="text"
                  value={currentPwd}
                  onChange={(e) => setCurrentPwd(e.target.value)}
                  data-testid="master-profile-current-pwd"
                  className="w-full px-3 py-2 rounded-xl border border-[#F59E0B]/40 focus:outline-none focus:ring-2 focus:ring-[#F59E0B] text-sm"
                  placeholder="كلمة السر الحالية"
                />
                <input
                  type="text"
                  value={newPwd}
                  onChange={(e) => setNewPwd(e.target.value)}
                  data-testid="master-profile-new-pwd"
                  className="w-full px-3 py-2 rounded-xl border border-[#F59E0B]/40 focus:outline-none focus:ring-2 focus:ring-[#F59E0B] text-sm"
                  placeholder="كلمة السر الجديدة (4 أحرف على الأقل)"
                />
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="border-t border-[#E4EBE6] px-5 py-3 flex items-center justify-between gap-2 bg-[#F9FAFB]">
            <button
              onClick={onClose}
              disabled={saving}
              className="px-4 py-2 text-sm font-bold text-[#5C6E64] hover:text-[#111814]"
            >
              إلغاء
            </button>
            <button
              onClick={save}
              disabled={saving}
              data-testid="master-profile-save"
              className="bg-[#15803D] hover:bg-[#126B32] disabled:opacity-60 text-white font-black px-6 py-2 rounded-full text-sm flex items-center gap-2 shadow-lg active:scale-95 transition-transform"
            >
              <Check className="w-4 h-4" />
              {saving ? "جاري الحفظ..." : "حفظ الملف"}
            </button>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default MasterProfileModal;
